/* eslint-disable react-hooks/set-state-in-effect -- legacy prefill effects; rebuilt in task 9 */
'use client';

import { useState, useEffect, useRef, useCallback } from 'react';
import Link from 'next/link';
import { Calendar, Clock, CheckCircle, AlertCircle, User, MessageSquare, LogIn } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { requestTour, getAvailableSlots } from '@/lib/api';
import { useAuthStore } from '@/stores/authStore';
import { consumeTourIntent, startGuestTourSignIn } from '@/lib/tourIntent';
import { userFullName } from '@/lib/user';
import { cn } from '@/lib/utils';
import type { CreateTourRequest, AvailableSlots } from '@/types/auth';

/**
 * What the buyer asks for when we could not read the calendar.
 *
 * A real, checkable request — the advisor agrees a time — rather than a specific
 * hour we would be inventing. Sent as `requested_time`, and it reads correctly in
 * the advisor email.
 */
const FIRST_AVAILABLE = 'First available';

interface TourRequestFormProps {
  listingId: string;
  listingAddress: string;
}

type FormStatus = 'idle' | 'loading' | 'success' | 'error';

export default function TourRequestForm({ listingId, listingAddress }: TourRequestFormProps) {
  const { user } = useAuthStore();
  // Some accounts have no name on file — fall back gracefully rather than
  // render "Booking as  (email)" or submit a nameless request.
  const fullName = user ? userFullName(user) : '';

  // Form state
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [selectedTime, setSelectedTime] = useState<string>('');
  const [guestName, setGuestName] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  const [guestPhone, setGuestPhone] = useState('');
  const [message, setMessage] = useState('');

  // UI state
  const [availableSlots, setAvailableSlots] = useState<string[]>([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  // A failed availability check is its own state. It used to be indistinguishable
  // from "the seller has no times", which is a claim about their calendar we were
  // making out of a network error.
  const [slotsError, setSlotsError] = useState(false);
  const [status, setStatus] = useState<FormStatus>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  // The date/time actually submitted — drives the confirmation copy. Kept
  // separate from the live selection so the date-change slot effect (which
  // resets selectedTime) can't blank out the success message on auto-resume.
  const [confirmed, setConfirmed] = useState<{ date: string; time: string } | null>(null);

  // Next 14 days for date selection. Built client-side only: "today" depends on
  // the viewer's timezone, so an ISR-cached server render would mismatch on
  // hydration. Values use LOCAL date parts — toISOString() is UTC and shifts
  // evening-Pacific picks to the next day.
  const [availableDates, setAvailableDates] = useState<
    { value: string; label: string; dayOfWeek: string }[]
  >([]);
  useEffect(() => {
    const dates: { value: string; label: string; dayOfWeek: string }[] = [];
    const today = new Date();

    for (let i = 1; i <= 14; i++) {
      const date = new Date(today.getFullYear(), today.getMonth(), today.getDate() + i);

      const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
      const dayOfWeek = date.toLocaleDateString('en-US', { weekday: 'short' });
      const label = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

      dates.push({ value, label, dayOfWeek });
    }

    setAvailableDates(dates);
  }, []);

  // Fetch available slots when date changes.
  //
  // This used to `catch` and hand back seven invented times — 9:00 AM through
  // 4:00 PM — so a buyer picked 2:00 PM from a list no calendar backed and
  // submitted a request against a slot nothing had checked. Fabricated
  // availability on the booking screen is the worst place in the product to
  // invent data. The failure is now a state, and it says so.
  const fetchSlots = useCallback(async () => {
    if (!selectedDate) return;
    setLoadingSlots(true);
    setSlotsError(false);
    setSelectedTime('');
    try {
      const response: AvailableSlots = await getAvailableSlots(listingId, selectedDate);
      setAvailableSlots(response.slots || []);
    } catch {
      setAvailableSlots([]);
      setSlotsError(true);
    } finally {
      setLoadingSlots(false);
    }
  }, [selectedDate, listingId]);

  useEffect(() => {
    if (!selectedDate) {
      setAvailableSlots([]);
      setSelectedTime('');
      setSlotsError(false);
      return;
    }
    void fetchSlots();
  }, [selectedDate, fetchSlots]);

  // Pre-fill logged in user data
  useEffect(() => {
    if (user) {
      setGuestName(`${user.first_name} ${user.last_name}`.trim());
      setGuestEmail(user.email);
      if (user.phone) {
        setGuestPhone(user.phone);
      }
    }
  }, [user]);

  // Auth-gated resume: a guest who chose "sign in to book faster" returns here
  // signed in. Restore their date/time/message and — when the selection is
  // complete — submit automatically so the tour *completes* on return. Runs
  // once, and only for an intent saved against THIS listing.
  const resumedRef = useRef(false);
  useEffect(() => {
    if (resumedRef.current || !user) return;
    const intent = consumeTourIntent();
    if (!intent) return;
    if (intent.listingId !== listingId) return; // not for this home — leave it
    resumedRef.current = true;

    if (intent.message) setMessage(intent.message);

    if (intent.date && intent.time) {
      // Complete selection — finish the booking now.
      setConfirmed({ date: intent.date, time: intent.time });
      setStatus('loading');
      requestTour({
        listing_id: listingId,
        requested_date: intent.date,
        requested_time: intent.time,
        message: intent.message || undefined,
        guest_name: `${user.first_name} ${user.last_name}`.trim() || undefined,
        guest_email: user.email,
        guest_phone: user.phone || undefined,
      })
        .then(() => setStatus('success'))
        .catch(() => {
          // Couldn't auto-complete — drop them back into the form with the date
          // restored so they can finish by hand (never a silent dead-end).
          setConfirmed(null);
          setSelectedDate(intent.date);
          setStatus('error');
          setErrorMessage(
            "We couldn't finish booking automatically — pick a time below and try again."
          );
        });
    } else if (intent.date) {
      // Incomplete selection — restore what we can and let them finish.
      setSelectedDate(intent.date);
    }
  }, [user, listingId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (!selectedDate || !selectedTime) {
      setErrorMessage('Please select a date and time for your tour.');
      setStatus('error');
      return;
    }

    if (!user && (!guestName || !guestEmail || !guestPhone)) {
      setErrorMessage('Please fill in all contact information.');
      setStatus('error');
      return;
    }

    // Signed in but no name on file — we ask for one inline so the advisor
    // knows who they're meeting.
    if (user && !fullName && !guestName.trim()) {
      setErrorMessage('Please add your name so your advisor knows who to greet.');
      setStatus('error');
      return;
    }

    setStatus('loading');
    setErrorMessage('');

    const tourData: CreateTourRequest = {
      listing_id: listingId,
      requested_date: selectedDate,
      requested_time: selectedTime,
      message: message || undefined,
    };

    // Only include guest info if not logged in or if different from user profile
    if (!user) {
      tourData.guest_name = guestName;
      tourData.guest_email = guestEmail;
      tourData.guest_phone = guestPhone;
    } else {
      // For logged-in users, still include their info for the tour record
      tourData.guest_name = guestName;
      tourData.guest_email = guestEmail;
      tourData.guest_phone = guestPhone;
    }

    try {
      await requestTour(tourData);
      setConfirmed({ date: selectedDate, time: selectedTime });
      setStatus('success');
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : 'Failed to submit tour request. Please try again.');
      setStatus('error');
    }
  };

  // Success state
  if (status === 'success') {
    const conf = confirmed ?? { date: selectedDate, time: selectedTime };
    return (
      <div className="glass rounded-card bg-surface/80 p-6 shadow-lift">
        <div className="text-center py-4">
          <div className="w-16 h-16 bg-emerald-500/15 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-emerald-300" />
          </div>
          <h3 className="font-display text-2xl text-ink mb-2">Tour Request Submitted!</h3>
          <p className="text-ink-muted mb-4">
            We&apos;ve received your request to tour <strong>{listingAddress}</strong> on{' '}
            <strong>{new Date(conf.date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</strong>{' '}
            at <strong>{conf.time}</strong>.
          </p>
          <p className="text-sm text-ink-muted">
            A GoReal advisor will reach out within 2 hours to confirm your tour.
          </p>
          {user && (
            <Button className="mt-6 w-full" variant="secondary" asChild>
              <Link href="/settings/tours">See all your tours</Link>
            </Button>
          )}
          <Button
            className="mt-3"
            variant="ghost"
            onClick={() => {
              setStatus('idle');
              setConfirmed(null);
              setSelectedDate('');
              setSelectedTime('');
              setMessage('');
            }}
          >
            Request another tour
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="glass rounded-card bg-surface/80 p-6 shadow-lift">
      <div className="flex items-center gap-2 mb-4">
        <Calendar className="w-5 h-5 text-emerald-300" />
        <h3 className="font-display text-xl text-ink">Schedule a Tour</h3>
      </div>

      <p className="text-ink-muted text-sm mb-6">
        Choose a time that works for you. Our tours are free and there&apos;s no obligation.
      </p>

      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Date Selection */}
        <div>
          <label className="block text-sm font-medium text-ink-soft mb-2">
            Select a date
          </label>
          <div className="grid grid-cols-4 gap-2">
            {availableDates.length === 0 &&
              Array.from({ length: 8 }, (_, i) => (
                <div key={i} className="h-[54px] animate-pulse rounded-lg border border-hairline bg-glass" />
              ))}
            {availableDates.slice(0, 8).map((date) => (
              <button
                key={date.value}
                type="button"
                // The visible text is two stacked nodes ("Mon" / "Aug 3"), which a
                // screen reader runs together as "MonAug 3".
                aria-label={`${date.dayOfWeek} ${date.label}`}
                onClick={() => setSelectedDate(date.value)}
                className={cn(
                  'p-2 rounded-lg border text-center transition-all',
                  'hover:border-emerald-500/50 hover:bg-emerald-500/10',
                  selectedDate === date.value
                    ? 'border-emerald-500/60 bg-emerald-500/12 ring-2 ring-emerald-500/40'
                    : 'border-hairline bg-glass'
                )}
              >
                <div className="text-xs text-ink-muted">{date.dayOfWeek}</div>
                <div className={cn(
                  'font-semibold text-sm',
                  selectedDate === date.value ? 'text-emerald-700' : 'text-ink'
                )}>
                  {date.label}
                </div>
              </button>
            ))}
          </div>
          {availableDates.length > 8 && (
            <details className="mt-2">
              <summary className="text-sm text-emerald-300 cursor-pointer hover:text-emerald-700">
                Show more dates
              </summary>
              <div className="grid grid-cols-4 gap-2 mt-2">
                {availableDates.slice(8).map((date) => (
                  <button
                    key={date.value}
                    type="button"
                    // The visible text is two stacked nodes ("Mon" / "Aug 3"), which a
                    // screen reader runs together as "MonAug 3".
                    aria-label={`${date.dayOfWeek} ${date.label}`}
                    onClick={() => setSelectedDate(date.value)}
                    className={cn(
                      'p-2 rounded-lg border text-center transition-all',
                      'hover:border-emerald-500/50 hover:bg-emerald-500/10',
                      selectedDate === date.value
                        ? 'border-emerald-500/60 bg-emerald-500/12 ring-2 ring-emerald-500/40'
                        : 'border-hairline bg-glass'
                    )}
                  >
                    <div className="text-xs text-ink-muted">{date.dayOfWeek}</div>
                    <div className={cn(
                      'font-semibold text-sm',
                      selectedDate === date.value ? 'text-emerald-700' : 'text-ink'
                    )}>
                      {date.label}
                    </div>
                  </button>
                ))}
              </div>
            </details>
          )}
        </div>

        {/* Time Slot Selection */}
        {selectedDate && (
          <div>
            <label className="block text-sm font-medium text-ink-soft mb-2">
              <Clock className="w-4 h-4 inline mr-1" />
              Select a time
            </label>
            {loadingSlots ? (
              <div className="flex items-center justify-center py-4">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-emerald-400"></div>
                <span className="ml-2 text-sm text-ink-muted">Loading available times...</span>
              </div>
            ) : availableSlots.length > 0 ? (
              <div className="grid grid-cols-3 gap-2">
                {availableSlots.map((slot) => (
                  <button
                    key={slot}
                    type="button"
                    onClick={() => setSelectedTime(slot)}
                    className={cn(
                      'py-2 px-3 rounded-lg border text-sm font-medium transition-all',
                      'hover:border-emerald-500/50 hover:bg-emerald-500/10',
                      selectedTime === slot
                        ? 'border-emerald-500/60 bg-emerald-500/12 text-emerald-700 ring-2 ring-emerald-500/40'
                        : 'border-hairline bg-glass text-ink-soft'
                    )}
                  >
                    {slot}
                  </button>
                ))}
              </div>
            ) : slotsError ? (
              // Honest about what happened, and still a way through: "First
              // available" says we will agree a time, where a grid of invented
              // slots said we already had.
              <div className="space-y-3 py-2">
                <p className="text-sm text-ink-muted">
                  We couldn&rsquo;t check available times just now.
                </p>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => void fetchSlots()}
                    className="rounded-lg border border-hairline bg-glass px-3 py-2 text-sm font-medium text-ink-soft transition-all hover:border-emerald-500/50 hover:bg-emerald-500/10"
                  >
                    Try again
                  </button>
                  <button
                    type="button"
                    onClick={() => setSelectedTime(FIRST_AVAILABLE)}
                    className={cn(
                      'rounded-lg border px-3 py-2 text-sm font-medium transition-all',
                      'hover:border-emerald-500/50 hover:bg-emerald-500/10',
                      selectedTime === FIRST_AVAILABLE
                        ? 'border-emerald-500/60 bg-emerald-500/12 text-emerald-700 ring-2 ring-emerald-500/40'
                        : 'border-hairline bg-glass text-ink-soft'
                    )}
                  >
                    {FIRST_AVAILABLE}
                  </button>
                </div>
              </div>
            ) : (
              <p className="text-sm text-ink-muted py-2">
                No available times for this date. Please select another date.
              </p>
            )}
          </div>
        )}

        {/* Contact Information - Show for guests or pre-filled for users */}
        {!user ? (
          <div className="space-y-4 pt-2 border-t border-hairline">
            {/* Auth-gated shortcut: sign in and the tour finishes on return with
                the chosen date/time kept — no details re-typed. */}
            <button
              type="button"
              onClick={() =>
                startGuestTourSignIn({
                  listingId,
                  date: selectedDate,
                  time: selectedTime,
                  message,
                })
              }
              className="flex w-full items-center justify-center gap-2 rounded-xl border border-emerald-500/40 bg-emerald-500/10 px-4 py-2.5 text-sm font-semibold text-emerald-700 transition-colors hover:bg-emerald-500/15"
            >
              <LogIn className="h-4 w-4" />
              Sign in &amp; we&apos;ll finish booking
            </button>
            <p className="text-center text-xs text-ink-faint">
              Have a GoReal account? We&apos;ll keep your date &amp; time and book it for you.
            </p>
            <div className="flex items-center gap-3">
              <span className="h-px flex-1 bg-hairline" />
              <span className="text-xs font-medium uppercase tracking-wider text-ink-faint">
                or continue as a guest
              </span>
              <span className="h-px flex-1 bg-hairline" />
            </div>
            <Input
              label="Full name"
              type="text"
              value={guestName}
              onChange={(e) => setGuestName(e.target.value)}
              placeholder="John Smith"
              required
            />
            <Input
              label="Email"
              type="email"
              value={guestEmail}
              onChange={(e) => setGuestEmail(e.target.value)}
              placeholder="john@example.com"
              required
            />
            <Input
              label="Phone"
              type="tel"
              value={guestPhone}
              onChange={(e) => setGuestPhone(e.target.value)}
              placeholder="(555) 123-4567"
              required
            />
          </div>
        ) : fullName ? (
          <div className="pt-2 border-t border-hairline">
            <div className="flex items-center gap-2 text-sm text-ink-muted glass rounded-lg p-3">
              <User className="w-4 h-4 text-ink-faint" />
              <span>Booking as <strong>{fullName}</strong> ({user.email})</span>
            </div>
          </div>
        ) : (
          <div className="space-y-4 pt-2 border-t border-hairline">
            <div className="flex items-center gap-2 text-sm text-ink-muted glass rounded-lg p-3">
              <User className="w-4 h-4 text-ink-faint" />
              <span>Signed in as <strong>{user.email}</strong></span>
            </div>
            <Input
              label="Your name"
              type="text"
              value={guestName}
              onChange={(e) => setGuestName(e.target.value)}
              placeholder="Your name"
              required
            />
          </div>
        )}

        {/* Message (optional) */}
        <div>
          <label className="block text-sm font-medium text-ink-soft mb-1">
            <MessageSquare className="w-4 h-4 inline mr-1" />
            Message (optional)
          </label>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Any questions or specific requests for the tour?"
            rows={3}
            className={cn(
              'w-full rounded-xl border border-hairline bg-glass px-3 py-2 text-sm text-ink',
              'placeholder:text-ink-faint',
              'focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500/60',
              'resize-none'
            )}
          />
        </div>

        {/* Error Message */}
        {status === 'error' && errorMessage && (
          <div className="flex items-start gap-2 p-3 bg-coral-500/10 border border-coral-500/30 rounded-lg">
            <AlertCircle className="w-5 h-5 text-coral-400 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-coral-300">{errorMessage}</p>
          </div>
        )}

        {/* Submit Button */}
        <Button
          type="submit"
          className="w-full"
          size="lg"
          disabled={status === 'loading' || !selectedDate || !selectedTime}
        >
          {status === 'loading' ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-canvas"></div>
              Submitting...
            </>
          ) : (
            <>
              <Calendar className="w-4 h-4" />
              Request Tour
            </>
          )}
        </Button>

        {/* Trust indicators */}
        <div className="text-center space-y-1">
          <p className="text-xs text-ink-faint">
            Free tour, no obligation
          </p>
          <p className="text-xs text-ink-faint">
            We&apos;ll confirm your appointment within 2 hours
          </p>
        </div>
      </form>
    </div>
  );
}
